import React, { useMemo, useState } from 'react';
import { UiComponent } from 'components';
import { IAdmin, RoleScope } from 'types';
import { AdminsQuery, OrganizationQuery, RoleQuery } from 'queries';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';

interface Props {
  adminId: number;
}

// 어드민에 배정된 role 표시 + 추가/해제.
// admin:update permission (PlatformSuper) 만 호출 가능 — 백엔드에서 차단.
function RoleAssign({ adminId }: Props) {
  const { data: admin } = AdminsQuery.useGet(adminId);
  const { data: orgs = [] } = OrganizationQuery.useGetAll();

  const scopeOptions = useMemo(() => {
    const list: { value: string; label: string; scope: RoleScope }[] = [];
    orgs.forEach((o) =>
      list.push({
        value: String(o.id),
        label: o.displayName,
        scope: o.id,
      }),
    );
    return list;
  }, [orgs]);

  const [scopeValue, setScopeValue] = useState<string>('');
  const selectedScope: RoleScope = Number(scopeValue);
  const { data: rolesInScope = [] } = RoleQuery.useGetAll(
    selectedScope,
    scopeValue !== '',
  );
  const [roleIdToAdd, setRoleIdToAdd] = useState<string>('');

  const assignedIds = new Set((admin?.roles ?? []).map((r) => r.id));
  const assignableRoles = rolesInScope.filter((r) => !assignedIds.has(r.id));

  const { mutate: assign } = AdminsQuery.useAssignRole();
  const { mutate: unassign } = AdminsQuery.useUnassignRole();

  const handleAdd = () => {
    if (!roleIdToAdd) return;
    assign(
      { adminId, roleId: Number(roleIdToAdd) },
      {
        onSuccess: () => {
          toast.success('Role 이 배정되었습니다');
          setRoleIdToAdd('');
        },
        onError: () => toast.error(DEFAULT_ERROR_MESSAGE),
      },
    );
  };

  const handleRemove = (roleId: number) => {
    unassign(
      { adminId, roleId },
      {
        onSuccess: () => toast.success('Role 이 해제되었습니다'),
        onError: () => toast.error(DEFAULT_ERROR_MESSAGE),
      },
    );
  };

  return (
    <div css={{ width: 500 }}>
      <p css={{ fontWeight: 600, marginBottom: 8 }}>할당된 Role</p>
      {(admin?.roles ?? []).length === 0 ? (
        <p css={{ color: '#888', fontSize: 13 }}>배정된 role 이 없습니다</p>
      ) : (
        <div css={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {admin?.roles?.map((r) => (
            <div
              key={r.id}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                background: '#f8f8fa',
                borderRadius: 4,
              }}
            >
              <div>
                <b>{r.name}</b>
                <span css={{ fontSize: 12, color: '#666', marginLeft: 8 }}>
                  {r.organization?.displayName}
                </span>
              </div>
              <UiComponent.Button
                variant="border"
                color="primary"
                onClick={() => handleRemove(r.id)}
              >
                해제
              </UiComponent.Button>
            </div>
          ))}
        </div>
      )}

      <p css={{ fontWeight: 600, margin: '20px 0 8px' }}>Role 추가</p>
      <div css={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          value={scopeValue}
          onChange={(e) => {
            setScopeValue(e.target.value);
            setRoleIdToAdd('');
          }}
          css={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4,
          }}
        >
          {scopeOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={roleIdToAdd}
          onChange={(e) => setRoleIdToAdd(e.target.value)}
          css={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4,
          }}
        >
          <option value="">role 선택</option>
          {assignableRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <UiComponent.Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
        >
          추가
        </UiComponent.Button>
      </div>
    </div>
  );
}

export default RoleAssign;
